import { projectApi, type ApiKey, type Project } from '@shared/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, FolderPlus, KeyRound, Trash2 } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useFormError } from '@/hooks/use-form-errors';
import { useSession } from '@/hooks/use-session';
import { formatRelative } from '@/lib/utils';
import { ConfirmDialog } from '../components/confirm-dialog';
import { ImportsSection } from '../components/imports-section';
import { IntegrationsSection } from '../components/integrations-section';
import { SettingsSection } from '../components/settings-section';

/**
 * Shown once, immediately after creation. The server stores only a hash, so
 * this is the single opportunity to copy the secret.
 */
const SecretKeyDialog = ({
  secretKey,
  onClose,
}: {
  secretKey: string | null;
  onClose: () => void;
}) => (
  <Dialog open={Boolean(secretKey)} onOpenChange={(open) => !open && onClose()}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Copy your secret key</DialogTitle>
        <DialogDescription>
          This is the only time it is shown. If you lose it, revoke the key and
          create another.
        </DialogDescription>
      </DialogHeader>

      <DialogBody>
        <div className="flex items-center gap-2">
          <code className="min-w-0 flex-1 overflow-x-auto rounded-lg border border-border bg-surface-muted px-3 py-2 font-mono text-xs text-ink">
            {secretKey}
          </code>
          <Button
            variant="secondary"
            size="icon"
            aria-label="Copy secret key"
            onClick={() => {
              if (secretKey) {
                void navigator.clipboard?.writeText(secretKey);
              }
            }}
          >
            <Copy />
          </Button>
        </div>
      </DialogBody>

      <DialogFooter>
        <Button onClick={onClose}>Done</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const ApiKeysSection = ({
  project,
  canManage,
  canCreate,
}: {
  project: Project;
  canManage: boolean;
  canCreate: boolean;
}) => {
  const queryClient = useQueryClient();
  const { error, captureError, clearError } = useFormError();
  const [newSecretKey, setNewSecretKey] = React.useState<string | null>(null);
  const [pendingRevoke, setPendingRevoke] = React.useState<ApiKey | null>(null);

  const keysQuery = useQuery({
    queryKey: ['api-keys', project.id],
    queryFn: () => projectApi.listApiKeys(project.id),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['api-keys', project.id] });
  };

  const createMutation = useMutation({
    mutationFn: () => projectApi.createApiKey(project.id, { name: 'Default' }),
    onSuccess: (created) => {
      setNewSecretKey(created.secretKey);
      invalidate();
    },
    onError: captureError,
  });

  const revokeMutation = useMutation({
    mutationFn: (apiKeyId: string) =>
      projectApi.revokeApiKey(project.id, apiKeyId),
    onSuccess: () => {
      setPendingRevoke(null);
      invalidate();
    },
    onError: (err) => {
      setPendingRevoke(null);
      captureError(err);
    },
  });

  const keys = keysQuery.data?.apiKeys ?? [];

  return (
    <div className="px-5 py-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-xs font-medium uppercase tracking-wide text-ink-subtle">
          API keys
        </h3>
        {canCreate ? (
          <Button
            variant="secondary"
            size="sm"
            loading={createMutation.isPending}
            onClick={() => {
              clearError();
              createMutation.mutate();
            }}
          >
            <KeyRound />
            New key
          </Button>
        ) : null}
      </div>

      {error ? (
        <Alert tone="error" className="mb-3">
          {error}
        </Alert>
      ) : null}

      {keysQuery.isLoading ? (
        <Skeleton className="h-12 w-full" />
      ) : keys.length === 0 ? (
        <p className="py-2 text-sm text-ink-muted">
          No keys yet. Create one to start sending events.
        </p>
      ) : (
        <ul className="space-y-2">
          {keys.map((key) => (
            <li
              key={key.id}
              className="flex items-center gap-3 rounded-lg border border-border bg-surface px-3 py-2.5"
            >
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 text-sm font-medium text-ink">
                  {key.name}
                  {key.revokedAt ? (
                    <Badge tone="critical">revoked</Badge>
                  ) : null}
                </p>
                <p className="mt-0.5 truncate font-mono text-xs text-ink-muted">
                  {key.publicKey}
                </p>
              </div>

              <span className="hidden text-xs text-ink-subtle sm:block">
                used {formatRelative(key.lastUsedAt)}
              </span>

              {canManage && !key.revokedAt ? (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Revoke ${key.name}`}
                  onClick={() => {
                    clearError();
                    setPendingRevoke(key);
                  }}
                >
                  <Trash2 />
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      <SecretKeyDialog
        secretKey={newSecretKey}
        onClose={() => setNewSecretKey(null)}
      />

      <ConfirmDialog
        open={Boolean(pendingRevoke)}
        onOpenChange={(open) => !open && setPendingRevoke(null)}
        title={`Revoke ${pendingRevoke?.name}?`}
        description="Anything using this key stops sending events immediately. Existing data is unaffected."
        confirmLabel="Revoke key"
        loading={revokeMutation.isPending}
        onConfirm={() => {
          if (pendingRevoke) {
            revokeMutation.mutate(pendingRevoke.id);
          }
        }}
      />
    </div>
  );
};

const CreateProjectSection = () => (
  <SettingsSection
    title="New project"
    description="Start with your website. We’ll prepare the right setup for your product."
  >
    <Button asChild variant="secondary">
      <Link to="/projects/new">
        <FolderPlus />
        Add project
      </Link>
    </Button>
  </SettingsSection>
);

export const ProjectsTab = () => {
  const { user } = useSession();
  const queryClient = useQueryClient();
  const { error, captureError, clearError } = useFormError();
  const [pendingDelete, setPendingDelete] = React.useState<Project | null>(
    null,
  );

  const role = user?.currentOrganization.role;
  const canManage = role === 'owner' || role === 'admin';
  const canCreateKeys = canManage || role === 'member';

  const projectsQuery = useQuery({
    queryKey: ['projects'],
    queryFn: projectApi.listProjects,
  });

  const deleteMutation = useMutation({
    mutationFn: (projectId: string) => projectApi.deleteProject(projectId),
    onSuccess: () => {
      setPendingDelete(null);
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: (err) => {
      setPendingDelete(null);
      captureError(err);
    },
  });

  const projects = projectsQuery.data?.projects ?? [];

  return (
    <div className="space-y-6">
      {error ? <Alert tone="error">{error}</Alert> : null}

      {projectsQuery.isLoading ? (
        <Skeleton className="h-40 w-full rounded-card" />
      ) : (
        projects.map((project) => (
          <div
            key={project.id}
            className="overflow-hidden rounded-card border border-border bg-surface"
          >
            <div className="flex items-start gap-3 px-5 py-4">
              <div className="min-w-0 flex-1">
                <h2 className="text-sm font-semibold text-ink">
                  {project.name}
                </h2>
                <p className="mt-1 text-sm text-ink-muted">
                  {project.primaryDomain ?? 'No primary domain set'}
                  <span className="mx-1.5 text-ink-subtle">&middot;</span>
                  {project.timezone}
                </p>
              </div>

              {canManage && projects.length > 1 ? (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Delete ${project.name}`}
                  onClick={() => {
                    clearError();
                    setPendingDelete(project);
                  }}
                >
                  <Trash2 />
                </Button>
              ) : null}
            </div>

            <ApiKeysSection
              project={project}
              canManage={canManage}
              canCreate={canCreateKeys}
            />

            <IntegrationsSection project={project} canManage={canManage} />

            <ImportsSection project={project} canManage={canManage} />
          </div>
        ))
      )}

      {canManage ? <CreateProjectSection /> : null}

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={`Delete ${pendingDelete?.name}?`}
        description="Its API keys stop working and it disappears from the product. Historical data is retained but no longer reachable."
        confirmLabel="Delete project"
        confirmationPhrase={pendingDelete?.name}
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (pendingDelete) {
            deleteMutation.mutate(pendingDelete.id);
          }
        }}
      />
    </div>
  );
};
